import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
} from "@mui/material";
import { GitHub as GitHubIcon, Lock as LockIcon } from "@mui/icons-material";
import useStore from "../../Store/Store";
import { useTheme } from "@mui/material/styles";

interface GitHubLoginSpotlightProps {
  open?: boolean;
  onClose?: () => void;
}

const GitHubLoginSpotlight: React.FC<GitHubLoginSpotlightProps> = ({
  open: propOpen,
  onClose: propOnClose,
}) => {
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(false);

  // Get state from store
  const { showGitHubLoginSpotlight, setShowGitHubLoginSpotlight } = useStore(
    (state) => ({
      showGitHubLoginSpotlight: state.showGitHubLoginSpotlight,
      setShowGitHubLoginSpotlight: state.setShowGitHubLoginSpotlight,
    })
  );

  // Use prop values if provided, otherwise use store values
  const open = propOpen !== undefined ? propOpen : showGitHubLoginSpotlight;
  const onClose =
    propOnClose ||
    (() => {
      setShowGitHubLoginSpotlight(false);
    });

  const handleGitHubLogin = () => {
    setIsLoading(true);
    const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    // Store current URL to redirect back after authentication
    sessionStorage.setItem("redirectUrl", window.location.href);
    // Also store as originalCodeCanvasUrl for first-time app installations
    sessionStorage.setItem("originalCodeCanvasUrl", window.location.href);
    window.location.assign(`${REACT_APP_BACKEND_URL}/auth/github`);
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          background: theme.palette.background.paper,
          boxShadow: theme.shadows[24],
        },
      }}
    >
      <DialogTitle sx={{ pb: 1, pt: 3 }}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <LockIcon sx={{ color: theme.palette.warning.main, fontSize: 28 }} />
          <Typography variant="h5" component="span" fontWeight="600">
            Repository Access Required
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2, pb: 2 }}>
        <DialogContentText sx={{ mb: 3, color: theme.palette.text.primary }}>
          The repository you're trying to access appears to be private or
          doesn't exist. If this is a private repository, you'll need to sign in
          with GitHub to access it.
        </DialogContentText>

        <Box
          sx={{
            bgcolor: theme.palette.action.hover,
            borderRadius: 2,
            p: 2,
            mb: 2,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>What happens when you sign in:</strong>
          </Typography>
          <Typography variant="body2" component="ul" sx={{ m: 0, pl: 2.5 }}>
            <li>You'll be redirected to GitHub to authenticate</li>
            <li>GitHub will ask for permission to access your repositories</li>
            <li>You'll be redirected back here automatically</li>
            <li>You'll be able to access your private repositories</li>
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1 }}>
        <Button
          onClick={handleCancel}
          variant="outlined"
          size="large"
          disabled={isLoading}
          sx={{
            borderRadius: 2,
            textTransform: "none",
            px: 3,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleGitHubLogin}
          variant="contained"
          size="large"
          disabled={isLoading}
          startIcon={
            isLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <GitHubIcon />
            )
          }
          sx={{
            mt: 2,
            mr: 2,
            borderRadius: 2,
            textTransform: "none",
            px: 3,
            bgcolor: theme.palette.mode === "dark" ? "#24292e" : "#24292e",
            color: "#ffffff",
            "&:hover": {
              bgcolor: theme.palette.mode === "dark" ? "#1a1e22" : "#1a1e22",
            },
          }}
        >
          {isLoading ? "Redirecting..." : "Sign in with GitHub"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GitHubLoginSpotlight;
